/**
 * Pure, DOM/CodeMirror-free scanning logic for `<!-- collapse -->`-marked
 * fenced code blocks in leedoc source text. Shared by the editor extensions
 * (collapse-fold.ts, code-block-edit.ts) and unit-testable in isolation.
 *
 * Leedoc markers are whole-line HTML comments, and their relative order before
 * a code block must not matter: `<!-- collapse -->` binds to the next code
 * fence, skipping blank lines and any other marker comments (e.g.
 * `<!-- tangle: init.sh -->`) in between.
 */

/**
 * A whole-line `<!-- collapse[: title][ | N] -->` marker (title/count optional,
 * whitespace tolerated around both). Group 1 captures N (the number of code
 * lines to keep visible), if given.
 */
export const COLLAPSE_LINE_RE = /^\s*<!--\s*collapse\s*(?::[^|>]*?)?\s*(?:\|\s*(\d+)\s*)?-->\s*$/;

/** Any whole-line HTML comment (other markers like `<!-- tangle: ... -->`). */
export const HTML_COMMENT_LINE_RE = /^\s*<!--.*-->\s*$/;

/** Default visible line count when the marker omits `| N` (matches CollapseEnricher). */
export const DEFAULT_VISIBLE_LINES = 15;

/** Opening code fence; group 1 is the fence run (``` or ~~~, 3+ chars). */
const FENCE_OPEN_RE = /^\s*(`{3,}|~{3,})/;
/** Closing code fence: a line that is only fence characters. */
const FENCE_CLOSE_RE = /^\s*(`{3,}|~{3,})\s*$/;

/**
 * A collapse-marked block's fold, as 1-based line numbers: the lines
 * `firstHiddenLine..lastHiddenLine` (inclusive) should be folded away.
 */
export interface CollapseFoldLines {
  firstHiddenLine: number;
  lastHiddenLine: number;
}

/**
 * Find every `<!-- collapse -->`-marked fenced code block whose body overflows
 * its visible-line budget (from the marker's `| N`, default 15), and return the
 * 1-based line ranges to fold. The opening/closing fences and the first N body
 * lines stay visible; blocks with at most N body lines yield no range.
 */
export function computeCollapseFoldLines(lines: string[]): CollapseFoldLines[] {
  const total = lines.length;
  const line = (no: number) => lines[no - 1]; // 1-based access
  const ranges: CollapseFoldLines[] = [];

  let n = 1;
  while (n <= total) {
    const marker = COLLAPSE_LINE_RE.exec(line(n));
    if (!marker) {
      n++;
      continue;
    }
    const visibleLines = marker[1] ? parseInt(marker[1], 10) : DEFAULT_VISIBLE_LINES;

    // Find the opening fence, skipping blank lines and other marker comments
    // (tangle, embed, …) after the collapse marker.
    let m = n + 1;
    while (m <= total && (line(m).trim() === '' || HTML_COMMENT_LINE_RE.test(line(m)))) m++;
    const open = m <= total ? FENCE_OPEN_RE.exec(line(m)) : null;
    if (!open) {
      n++;
      continue;
    }
    const fenceChar = open[1][0];
    const fenceLen = open[1].length;

    // Find the matching closing fence (same char, at least as long).
    let closeLineNo = 0;
    for (let k = m + 1; k <= total; k++) {
      const close = FENCE_CLOSE_RE.exec(line(k));
      if (close && close[1][0] === fenceChar && close[1].length >= fenceLen) {
        closeLineNo = k;
        break;
      }
    }
    if (!closeLineNo) {
      n++;
      continue;
    }

    // Code body is lines m+1 .. closeLineNo-1. Only fold when it overflows N,
    // then keep the first N visible and fold the rest up to the last body line
    // (fences stay visible).
    const lastBodyLineNo = closeLineNo - 1;
    const bodyLineCount = lastBodyLineNo - m;
    if (bodyLineCount > visibleLines && visibleLines >= 0) {
      ranges.push({ firstHiddenLine: m + visibleLines + 1, lastHiddenLine: lastBodyLineNo });
    }
    n = closeLineNo + 1;
  }

  return ranges;
}
